import type { LocationQuery, LocationQueryRaw } from 'vue-router'

import type { BrandAppTabId } from './brandAppContract'
import type { PanelTab } from './brandAppSections'

export const EDITOR_MODES = ['screen', 'theme', 'structure', 'share', 'settings'] as const

export type EditorMode = (typeof EDITOR_MODES)[number]

export interface EditorLocation {
  mode: EditorMode
  screen: BrandAppTabId
  section: string | null
  tab: PanelTab
}

const EDITOR_KEYS = ['mode', 'screen', 'section', 'tab'] as const

const single = (value: LocationQuery[string] | undefined): string | null =>
  typeof value === 'string' ? value : Array.isArray(value) && typeof value[0] === 'string' ? value[0] : null

export const toEditorQuery = (location: EditorLocation): Record<string, string> => ({
  ...(location.mode === 'screen' ? { screen: location.screen } : { mode: location.mode }),
  ...(location.section ? { section: location.section } : {}),
  ...(location.section && location.tab === 'text' ? { tab: 'text' } : {})
})

export const withEditorQuery = (query: LocationQuery, location: EditorLocation): LocationQueryRaw => {
  const rest = Object.fromEntries(
    Object.entries(query).filter(([key]) => !(EDITOR_KEYS as readonly string[]).includes(key))
  )

  return { ...rest, ...toEditorQuery(location) }
}

export const parseEditorQuery = (
  query: LocationQuery,
  fallback: EditorLocation,
  isValid: { screen: (id: string) => boolean; section: (id: string) => boolean }
): EditorLocation => {
  const mode = single(query.mode)
  const screen = single(query.screen)
  const section = single(query.section)

  return {
    mode: mode && (EDITOR_MODES as readonly string[]).includes(mode) ? (mode as EditorMode) : 'screen',
    screen: screen && isValid.screen(screen) ? (screen as BrandAppTabId) : fallback.screen,
    section: section && isValid.section(section) ? section : null,
    tab: single(query.tab) === 'text' ? 'text' : 'content'
  }
}

export const isSameEditorLocation = (a: EditorLocation, b: EditorLocation): boolean =>
  JSON.stringify(toEditorQuery(a)) === JSON.stringify(toEditorQuery(b))

export const onlyTabChanged = (from: EditorLocation, to: EditorLocation): boolean =>
  from.mode === to.mode && from.screen === to.screen && from.section === to.section && from.tab !== to.tab
